import styled from "styled-components/macro";
import { Avatar } from "../Avatar/Avatar";
import { TextBaseRegular, TextSmRegular } from "../Typography/TypographyV2";

export type MenuItemAssigneeProps = {
  /** Person row vs clear-assignee row. */
  type?: "assigned" | "unassigned";
  name?: string;
  email?: string | null;
  unassignedLabel?: string;
  onClick?: () => void;
  className?: string;
  "data-testid"?: string;
};

const Row = styled.button.attrs({ type: "button" })`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  width: 100%;
  margin: 0;
  padding: ${({ theme }) => theme.resolve("spacing_md")}
    ${({ theme }) => theme.resolve("spacing_lg")};
  border: none;
  background-color: ${({ theme }) =>
    theme.resolve("color_bg_surface_standard")};
  text-align: left;
  font: inherit;
  cursor: pointer;
  outline: none;
  appearance: none;
  -webkit-appearance: none;

  &:hover,
  &:focus-visible {
    background-color: ${({ theme }) => theme.resolve("color_bg_surface_hover")};
  }
`;

const Stack = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
`;

const PrimaryText = styled(TextBaseRegular)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const SupportingText = styled(TextSmRegular)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

/**
 * Person row inside `AssigneePopOver` (avatar + name + optional email).
 *
 * @see Agilis Design System — menuItemAssignee
 */
export function MenuItemAssignee({
  type = "assigned",
  name = "",
  email,
  unassignedLabel = "Unassigned",
  onClick,
  className,
  "data-testid": dataTestId,
}: MenuItemAssigneeProps) {
  const isUnassigned = type === "unassigned";
  const label = isUnassigned ? unassignedLabel : name;

  return (
    <Row
      className={className}
      data-testid={dataTestId}
      onClick={onClick}
      aria-label={label}
    >
      <Avatar
        type={isUnassigned ? "unassigned" : "text"}
        name={isUnassigned ? undefined : name}
        size="small"
      />
      <Stack>
        <PrimaryText color="primary">{label}</PrimaryText>
        {!isUnassigned && email ? (
          <SupportingText color="tertiary">{email}</SupportingText>
        ) : null}
      </Stack>
    </Row>
  );
}
